import { Injectable } from "@angular/core";
import { toEpoch } from "@firm360/libs/util/formatting";
import type { PendoProfile } from "../../pendo-profile.model";
import { AbstractServerProfileSource } from "./abstract-server.pendo-profile-source";

/** The server's full profile of the signed-in firm user. */
@Injectable()
export class CurrentServerProfileSource extends AbstractServerProfileSource<PendoProfileDTO> {
    protected readonly url = "api/observability/pendo/profile";

    protected translateResponse({
        visitor,
        account,
    }: PendoProfileDTO): PendoProfile {
        if (!visitor?.id || !account?.id) {
            throw new Error("Pendo profile response carries no ids");
        }
        return {
            visitor: {
                id: visitor.id,
                email: visitor.email ?? null,
                firstName: visitor.firstName ?? null,
                lastName: visitor.lastName ?? null,
                createdAt: toEpoch(visitor.createdDate),
                ...(visitor.lastLogin === undefined
                    ? {}
                    : { lastLoginAt: toEpoch(visitor.lastLogin) }),
                internal: !!visitor.internal,
                ...(visitor.authorities
                    ? { authorities: visitor.authorities }
                    : {}),
            },
            account: {
                id: account.id,
                name: account.name ?? null,
                subdomain: account.subdomain ?? null,
                createdAt: toEpoch(account.createdDate),
                planTier: account.planTier ?? null,
                beta: account.beta ?? null,
                demo: account.demo ?? null,
                cancelledAt: toEpoch(account.cancellationDate),
                ...(account.modules ? { modules: account.modules } : {}),
            },
        };
    }
}

interface PendoProfileDTO {
    visitor?: {
        id?: string;
        email?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        createdDate?: string | null;
        lastLogin?: string | null;
        internal?: boolean;
        authorities?: string[] | null;
    };
    account?: {
        id?: string;
        name?: string | null;
        subdomain?: string | null;
        createdDate?: string | null;
        planTier?: string | null;
        beta?: boolean | null;
        demo?: boolean | null;
        cancellationDate?: string | null;
        modules?: PendoProfile.Modules | null;
    };
}
